import { OrgEmployeeData, OrgStaffSlot, OrgTreeData, OrgUnitData, ROOT_ID } from "./types";

/**
 * Дерево структуры и его раскладка — чистые функции без React.
 *
 * Дерево горизонтальное: каждый уровень вложенности — колонка, рост вправо. У узла
 * в одной колонке сначала сотрудники, затем вложенные подразделения (у дирекции
 * может быть и свой штат, и подчинённые департаменты). Родитель центрируется по
 * первому и последнему потомку, листья укладываются сверху вниз — как в прототипе к ТЗ.
 *
 * Штат: ставку занимает действующий сотрудник подразделения с той же должностью (ранг не важен,
 * совместитель — тоже одна ставка). Незанятые ставки — псевдо-карточки «Вакансия» после сотрудников.
 */

export type TreeItemKind = "org" | "unit" | "employee" | "vacancy";

export interface TreeStats {
    /** действующих сотрудников во всей ветке */
    employees: number;
    /** вложенных подразделений во всей ветке */
    units: number;
    /** незанятых ставок во всей ветке */
    vacancies: number;
}

/** Штат самого подразделения (без вложенных) */
export interface StaffSummary {
    /** ставок по штату */
    total: number;
    /** занято сотрудниками */
    occupied: number;
    /** сотрудников сверх штата: должности нет в штате или на ней ставок меньше, чем людей */
    over: number;
}

export interface TreeItem {
    id: string;
    kind: TreeItemKind;
    parentId: string | null;
    /** 0 — организация, 1 — верхний уровень подразделений */
    depth: number;
    unit?: OrgUnitData;
    employee?: OrgEmployeeData;
    /** незанятая ставка */
    vacancy?: OrgStaffSlot;
    /** у подразделения с заданным штатом */
    staff?: StaffSummary;
    /** сначала сотрудники, затем вакансии, затем подразделения */
    children: TreeItem[];
    stats: TreeStats;
}

export interface OrgTree {
    root: TreeItem;
    byId: Map<string, TreeItem>;
}

export interface TreeFilter {
    /** юрлицо: пусто — все */
    organizationId: string;
    showFired: boolean;
}

const byOrderThenName = (a: OrgUnitData, b: OrgUnitData) =>
    (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER) || a.name.localeCompare(b.name, "ru");

export const sortUnits = (units: OrgUnitData[]) => [...units].sort(byOrderThenName);

export function buildTree(data: OrgTreeData, filter: TreeFilter): OrgTree {
    const byId = new Map<string, TreeItem>();
    const unitIds = new Set(data.units.map((u) => u.id));

    const childUnits = new Map<string, OrgUnitData[]>();
    data.units.forEach((u) => {
        // родитель удалён или скрыт — показываем узел на верхнем уровне, а не теряем
        const parent = u.parentId && unitIds.has(u.parentId) ? u.parentId : ROOT_ID;
        const list = childUnits.get(parent) ?? [];
        list.push(u);
        childUnits.set(parent, list);
    });

    const staff = new Map<string, OrgEmployeeData[]>();
    data.employees.forEach((e) => {
        if (e.fired && !filter.showFired) {
            return;
        }
        if (filter.organizationId && e.organizationId !== filter.organizationId) {
            return;
        }
        const list = staff.get(e.departmentId) ?? [];
        list.push(e);
        staff.set(e.departmentId, list);
    });

    // занятость ставок — по всем действующим сотрудникам, фильтры дерева на неё не влияют
    const occupied = new Map<string, Map<string, number>>();
    data.employees.forEach((e) => {
        if (e.fired) {
            return;
        }
        const byPosition = occupied.get(e.departmentId) ?? new Map<string, number>();
        const key = e.positionId ?? "";
        byPosition.set(key, (byPosition.get(key) ?? 0) + 1);
        occupied.set(e.departmentId, byPosition);
    });

    const root: TreeItem = {
        id: ROOT_ID,
        kind: "org",
        parentId: null,
        depth: 0,
        children: [],
        stats: { employees: 0, units: 0, vacancies: 0 },
    };
    byId.set(ROOT_ID, root);

    const visited = new Set<string>();

    const grow = (item: TreeItem, unitId: string) => {
        const manager = item.unit?.managerId;
        const people = (staff.get(unitId) ?? []).sort(
            (a, b) =>
                Number(b.id === manager) - Number(a.id === manager) ||
                Number(a.fired) - Number(b.fired) ||
                a.name.localeCompare(b.name, "ru")
        );

        people.forEach((employee) => {
            const child: TreeItem = {
                id: employee.id,
                kind: "employee",
                parentId: item.id,
                depth: item.depth + 1,
                employee,
                children: [],
                stats: { employees: employee.fired ? 0 : 1, units: 0, vacancies: 0 },
            };
            byId.set(child.id, child);
            item.children.push(child);
        });

        const slots = item.unit?.staff ?? [];
        if (slots.length) {
            const byPosition = occupied.get(unitId) ?? new Map<string, number>();
            const summary: StaffSummary = { total: 0, occupied: 0, over: 0 };
            byPosition.forEach((n, positionId) => {
                const slot = slots.find((s) => s.positionId === positionId);
                summary.over += Math.max(0, n - (slot?.count ?? 0));
            });
            slots.forEach((slot) => {
                const taken = Math.min(slot.count, byPosition.get(slot.positionId) ?? 0);
                summary.total += slot.count;
                summary.occupied += taken;
                for (let i = 0; i < slot.count - taken; i++) {
                    const child: TreeItem = {
                        id: vacancyId(unitId, slot.positionId, i),
                        kind: "vacancy",
                        parentId: item.id,
                        depth: item.depth + 1,
                        vacancy: slot,
                        children: [],
                        stats: { employees: 0, units: 0, vacancies: 1 },
                    };
                    byId.set(child.id, child);
                    item.children.push(child);
                }
            });
            item.staff = summary;
        }

        sortUnits(childUnits.get(unitId) ?? []).forEach((unit) => {
            // защита от цикла в данных: узел не может встретиться дважды
            if (visited.has(unit.id)) {
                return;
            }
            visited.add(unit.id);

            const child: TreeItem = {
                id: unit.id,
                kind: "unit",
                parentId: item.id,
                depth: item.depth + 1,
                unit,
                children: [],
                stats: { employees: 0, units: 0, vacancies: 0 },
            };
            byId.set(child.id, child);
            grow(child, unit.id);

            // фильтр по юрлицу: ветки без его сотрудников прячем, пустые (новые) узлы оставляем
            if (filter.organizationId && child.stats.employees === 0 && hasAnyStaff(unit.id)) {
                removeBranch(child);
                return;
            }

            item.children.push(child);
        });

        item.stats = item.children.reduce(
            (acc, c) => ({
                employees: acc.employees + c.stats.employees,
                units: acc.units + c.stats.units + (c.kind === "unit" ? 1 : 0),
                vacancies: acc.vacancies + c.stats.vacancies,
            }),
            { employees: 0, units: 0, vacancies: 0 }
        );
    };

    const allStaff = new Map<string, number>();
    data.employees.forEach((e) => allStaff.set(e.departmentId, (allStaff.get(e.departmentId) ?? 0) + 1));
    const hasAnyStaffCache = new Map<string, boolean>();
    function hasAnyStaff(unitId: string): boolean {
        const cached = hasAnyStaffCache.get(unitId);
        if (cached !== undefined) {
            return cached;
        }
        const result = (allStaff.get(unitId) ?? 0) > 0 || (childUnits.get(unitId) ?? []).some((u) => hasAnyStaff(u.id));
        hasAnyStaffCache.set(unitId, result);
        return result;
    }

    function removeBranch(item: TreeItem) {
        byId.delete(item.id);
        item.children.forEach(removeBranch);
    }

    grow(root, ROOT_ID);

    return { root, byId };
}

export const vacancyId = (unitId: string, positionId: string, index: number) => `vac:${unitId}:${positionId}:${index}`;

/** Подпись вакансии: «должность · ранг» */
export const vacancyRole = (slot: OrgStaffSlot) => [slot.positionName, slot.gradeName].filter(Boolean).join(" · ");

/** Содержимое узла, которое мешает его удалить: вакансии не в счёт */
export const hasRealContent = (item: TreeItem) => item.children.some((c) => c.kind !== "vacancy");

/** Путь от узла до корня (сам узел первым) */
export function ancestors(tree: OrgTree, id: string): string[] {
    const result: string[] = [];
    let current = tree.byId.get(id);
    let guard = 0;
    while (current && guard++ < 1000) {
        result.push(current.id);
        current = current.parentId ? tree.byId.get(current.parentId) : undefined;
    }
    return result;
}

/** Узел и все его потомки */
export function descendants(item: TreeItem): Set<string> {
    const result = new Set<string>();
    const walk = (i: TreeItem) => {
        result.add(i.id);
        i.children.forEach(walk);
    };
    walk(item);
    return result;
}

/** Сотрудники узла и вложенных — кандидаты в руководители */
export function branchEmployees(item: TreeItem): OrgEmployeeData[] {
    const result: OrgEmployeeData[] = [];
    const walk = (i: TreeItem) => {
        if (i.employee && !i.employee.fired) {
            result.push(i.employee);
        }
        i.children.forEach(walk);
    };
    walk(item);
    return result.sort((a, b) => a.name.localeCompare(b.name, "ru"));
}

/** Высота ветки в уровнях подразделений: 1 — у узла нет вложенных */
export function unitHeight(item: TreeItem): number {
    const units = item.children.filter((c) => c.kind === "unit");
    return 1 + (units.length ? Math.max(...units.map(unitHeight)) : 0);
}

const normalize = (value: string) => value.toLowerCase().replace(/ё/g, "е");

/** Поиск: подразделение по названию/руководителю, сотрудник по ФИО/должности */
export function searchTree(tree: OrgTree, query: string, employees: OrgEmployeeData[]): Set<string> {
    const q = normalize(query.trim());
    const result = new Set<string>();
    if (!q) {
        return result;
    }

    const names = new Map(employees.map((e) => [e.id, e.name]));

    tree.byId.forEach((item) => {
        let text = "";
        if (item.unit) {
            text = `${item.unit.name} ${item.unit.managerId ? (names.get(item.unit.managerId) ?? "") : ""}`;
        } else if (item.employee) {
            text = `${item.employee.name} ${item.employee.positionName ?? ""}`;
        } else if (item.vacancy) {
            text = `вакансия ${item.vacancy.positionName}`;
        }
        if (text && normalize(text).includes(q)) {
            result.add(item.id);
        }
    });

    return result;
}

/* ---------------- раскладка ---------------- */

export type LayoutKind = TreeItemKind | "add";

export interface LayoutNode {
    id: string;
    kind: LayoutKind;
    /** узел дерева; у строки «+ добавить» — узел, куда добавляем */
    item: TreeItem;
    parentId: string | null;
    depth: number;
    x: number;
    y: number;
    w: number;
    h: number;
    open: boolean;
    /** есть что раскрывать */
    expandable: boolean;
}

export interface LayoutEdge {
    id: string;
    source: string;
    target: string;
    /** глубина родителя: 0 — от организации */
    level: number;
}

export interface LayoutSizes {
    org: { w: number; h: number };
    unit: { w: number; h: number };
    employee: { w: number; h: number };
    add: { w: number; h: number };
    gapX: number;
    gapY: number;
    /** дополнительный зазор после состава раскрытого подразделения: сотрудники соседних отделов не сливаются в один столбец */
    groupGap: number;
}

export const NORMAL_SIZES: LayoutSizes = {
    org: { w: 220, h: 104 },
    // высота подразделения — при названии в одну строку; вторая строка добавляется через unitCardHeight
    unit: { w: 300, h: 70 },
    employee: { w: 264, h: 44 },
    add: { w: 264, h: 32 },
    gapX: 110,
    // ярлычки на верхней рамке сотрудника и кнопки «+» над подразделением выступают на ~8 px — зазор их вмещает
    gapY: 10,
    groupGap: 14,
};

export interface LayoutOptions {
    sizes: LayoutSizes;
    expanded: Set<string>;
    /** режим редактирования: строки «+ добавить», любой узел раскрывается */
    edit: boolean;
    /** высота карточки сотрудника, если она зависит от текста (должность переносится); по умолчанию — из sizes */
    employeeHeight?: (item: TreeItem) => number;
    /** высота карточки подразделения (название в одну или две строки); по умолчанию — из sizes */
    unitCardHeight?: (item: TreeItem) => number;
}

/** вторая строка карточки сотрудника: «должность · ранг» */
export const employeeRole = (employee: OrgEmployeeData) =>
    [employee.positionName, employee.gradeName].filter(Boolean).join(" · ") || "Должность не указана";

/**
 * Сколько строк займёт текст при переносе по словам в ширину width (как white-space: normal
 * с overflow-wrap: anywhere): слово длиннее строки режется по буквам.
 */
export function countLines(text: string, width: number, measure: (s: string) => number): number {
    const words = text.split(/\s+/).filter(Boolean);
    let lines = words.length ? 1 : 0;
    let line = "";
    for (const word of words) {
        const candidate = line ? `${line} ${word}` : word;
        if (measure(candidate) <= width) {
            line = candidate;
            continue;
        }
        if (line) {
            lines++;
        }
        line = "";
        for (const ch of word) {
            if (line && measure(line + ch) > width) {
                lines++;
                line = ch;
            } else {
                line += ch;
            }
        }
    }
    return Math.max(lines, 1);
}

export const addRowId = (id: string) => `add:${id}`;

export function layoutTree(tree: OrgTree, options: LayoutOptions) {
    const { sizes, expanded, edit, employeeHeight, unitCardHeight } = options;
    const nodes: LayoutNode[] = [];
    const edges: LayoutEdge[] = [];
    // следующая свободная точка по вертикали: листья укладываются сверху вниз
    let cursor = 0;

    const walk = (item: TreeItem, x: number): LayoutNode => {
        const kind = item.kind;
        const card = kind === "employee" || kind === "vacancy";
        const size = card
            ? { ...sizes.employee, h: employeeHeight ? employeeHeight(item) : sizes.employee.h }
            : kind === "unit" && unitCardHeight
              ? { ...sizes.unit, h: unitCardHeight(item) }
              : sizes[kind];
        const expandable = item.children.length > 0 || (edit && !card);
        const open = expandable && (kind === "org" || expanded.has(item.id));
        // места под кнопки «+ Подразделение / + Сотрудник» не резервируем: они появляются при наведении
        // на верхней рамке карточки и на время наведения могут перекрыть край соседа сверху
        const top = cursor;
        const mark = nodes.length;

        const node: LayoutNode = {
            id: item.id,
            kind,
            item,
            parentId: item.parentId,
            depth: item.depth,
            x,
            y: 0,
            w: size.w,
            h: size.h,
            open,
            expandable,
        };

        const kids: LayoutNode[] = [];
        if (open) {
            const childX = x + size.w + sizes.gapX;
            // у непустого узла «+ добавить» не ставим строкой под детьми: у большого отдела она уезжала
            // далеко вниз и путалась с соседними блоками - кнопки висят над самой карточкой
            item.children.forEach((child) => kids.push(walk(child, childX)));
        }

        if (kids.length) {
            const first = kids[0];
            const last = kids[kids.length - 1];
            node.y = (first.y + first.h / 2 + last.y + last.h / 2) / 2 - size.h / 2;

            // родитель выше потомков (он выше карточки сотрудника) — опускаем всю ветку,
            // иначе он наедет на соседа сверху
            if (node.y < top) {
                const shift = top - node.y;
                node.y = top;
                for (let i = mark; i < nodes.length; i++) {
                    nodes[i].y += shift;
                }
                cursor += shift;
            }
            cursor = Math.max(cursor, node.y + size.h + sizes.gapY);
            if (kind === "unit") {
                cursor += sizes.groupGap;
            }

            kids.forEach((kid) => {
                if (kid.kind !== "add") {
                    edges.push({ id: `${item.id}->${kid.id}`, source: item.id, target: kid.id, level: item.depth });
                }
            });
        } else {
            node.y = top;
            cursor = top + size.h + sizes.gapY;
        }

        nodes.push(node);
        return node;
    };

    walk(tree.root, 0);

    return { nodes, edges };
}
